import { useTranslation } from "react-i18next";
import {
  Building2,
  Briefcase,
  ShieldCheck,
  Cloud,
  Landmark,
  HeartPulse,
  Sprout,
  Building,
  CreditCard,
  Flag,
  Users,
  Scale,
  Shield,
} from "lucide-react";
import LocaleLink from "@/components/LocaleLink";

interface MegaMenuSolutionsProps {
  onItemClick?: () => void;
}

const MegaMenuSolutions = ({ onItemClick }: MegaMenuSolutionsProps) => {
  const { t } = useTranslation();

  // Top 5 most-potent industries by deal-unblocking potency.
  // Long-tail industries (Cybersecurity, Africa, regional packs) live in the footer.
  const industries = [
    {
      to: "/for/saas",
      icon: Cloud,
      label: t("megaMenu.industrySaas"),
      desc: t("megaMenu.industrySaasDesc"),
    },
    {
      to: "/for/fintech",
      icon: CreditCard,
      label: t("megaMenu.industryFintech"),
      desc: t(
        "megaMenu.industryFintechDesc"
      ),
    },
    {
      to: "/for/financial-services",
      icon: Landmark,
      label: t("megaMenu.industryFinancialServices"),
      desc: t(
        "megaMenu.industryFinancialServicesDesc"
      ),
    },
    {
      to: "/for/healthcare",
      icon: HeartPulse,
      label: t("megaMenu.industryHealthcare"),
      desc: t(
        "megaMenu.industryHealthcareDesc"
      ),
    },
    {
      to: "/for/public-sector",
      icon: Flag,
      label: t("megaMenu.industryPublicSector"),
      desc: t(
        "megaMenu.industryPublicSectorDesc"
      ),
    },
  ];

  const companySize = [
    {
      to: "/for/small-business",
      icon: Sprout,
      label: t("megaMenu.sizeSmallBusiness"),
      desc: t("megaMenu.sizeSmallBusinessDesc"),
    },
    {
      to: "/for/mid-market",
      icon: Building,
      label: t("megaMenu.sizeMidMarket"),
      desc: t("megaMenu.sizeMidMarketDesc"),
    },
    {
      to: "/enterprise",
      icon: Building2,
      label: t("megaMenu.sizeEnterprise"),
      desc: t("megaMenu.sizeEnterpriseDesc"),
    },
    {
      to: "/regulated-enterprises",
      icon: ShieldCheck,
      label: t("megaMenu.sizeRegulated"),
      desc: t("megaMenu.sizeRegulatedDesc"),
    },
  ];

  // Buyer-role-led audience column (per persona research).
  const audience = [
    {
      to: "/use-cases?role=ciso",
      icon: Shield,
      label: t("megaMenu.roleCiso"),
      desc: t("megaMenu.roleCisoDesc"),
    },
    {
      to: "/use-cases?role=cco",
      icon: Users,
      label: t("megaMenu.roleCco"),
      desc: t("megaMenu.roleCcoDesc"),
    },
    {
      to: "/use-cases?role=legal",
      icon: Scale,
      label: t("megaMenu.roleLegal"),
      desc: t("megaMenu.roleLegalDesc"),
    },
    {
      to: "/career-transition",
      icon: Briefcase,
      label: t("megaMenu.careerTransition"),
      desc: t("megaMenu.careerTransitionDesc"),
    },
    {
      to: "/why-csquare",
      icon: ShieldCheck,
      label: t("megaMenu.whyCsquareSolutions"),
      desc: t("megaMenu.whyCsquareSolutionsDesc"),
    },
  ];

  return (
    <div className="w-[820px] p-6">
      <div className="grid grid-cols-3 gap-6">
        {/* By industry */}
        <div>
          <h3 className="eyebrow mb-4">
            {t("megaMenu.byIndustry")}
          </h3>
          <div className="space-y-1">
            {industries.map(({ to, icon: Icon, label, desc }) => (
              <LocaleLink
                key={to}
                to={to}
                onClick={onItemClick}
                className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
              >
                <Icon className="h-5 w-5 text-accent mt-0.5" />
                <div>
                  <div className="font-medium text-sm group-hover:text-primary">{label}</div>
                  <div className="text-xs text-muted-foreground">{desc}</div>
                </div>
              </LocaleLink>
            ))}
          </div>
        </div>

        {/* By company size */}
        <div>
          <h3 className="eyebrow mb-4">
            {t("megaMenu.byCompanySize")}
          </h3>
          <div className="space-y-1">
            {companySize.map(({ to, icon: Icon, label, desc }) => (
              <LocaleLink
                key={to}
                to={to}
                onClick={onItemClick}
                className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
              >
                <Icon className="h-5 w-5 text-accent mt-0.5" />
                <div>
                  <div className="font-medium text-sm group-hover:text-primary">{label}</div>
                  <div className="text-xs text-muted-foreground">{desc}</div>
                </div>
              </LocaleLink>
            ))}
          </div>
        </div>

        {/* By audience (role-led) */}
        <div>
          <h3 className="eyebrow mb-4">
            {t("megaMenu.byAudience")}
          </h3>
          <div className="space-y-1">
            {audience.map(({ to, icon: Icon, label, desc }) => (
              <LocaleLink
                key={to}
                to={to}
                onClick={onItemClick}
                className="flex items-start gap-3 p-2.5 hover:bg-secondary transition-colors group"
              >
                <Icon className="h-5 w-5 text-accent mt-0.5" />
                <div>
                  <div className="font-medium text-sm group-hover:text-primary">{label}</div>
                  <div className="text-xs text-muted-foreground">{desc}</div>
                </div>
              </LocaleLink>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MegaMenuSolutions;
